import * as THREE from "three";
import {
	EffectComposer,
	EffectPass,
	RenderPass,
	BloomEffect,
	DepthOfFieldEffect,
	VignetteEffect,
	BrightnessContrastEffect,
} from "postprocessing";

/* -------------------------------------------------------------------------
   Botanical Drift — instanced 3D petal field
   ------------------------------------------------------------------------- */

const PALETTE: [number, number, number, number][] = [
	// [r, g, b, weight%]
	[0xe8 / 255, 0xb8 / 255, 0xc5 / 255, 50], // dusty rose
	[0xa8 / 255, 0xd5 / 255, 0xb8 / 255, 25], // sage mint
	[0xf5 / 255, 0xe8 / 255, 0xed / 255, 15], // cream
	[0xc8 / 255, 0xf0 / 255, 0x4a / 255, 10], // lime (rare accent)
];

function pickColor(): THREE.Color {
	const roll = Math.random() * 100;
	let acc = 0;
	for (const [r, g, b, w] of PALETTE) {
		acc += w;
		if (roll <= acc) return new THREE.Color(r, g, b);
	}
	return new THREE.Color(PALETTE[0][0], PALETTE[0][1], PALETTE[0][2]);
}

interface PetalDatum {
	pos: THREE.Vector3;
	vel: THREE.Vector3;
	rot: THREE.Euler;
	rotSpeed: THREE.Vector3;
	scale: number;
	phase: number;
	freq: number;
}

const COUNT_DESKTOP = 220;
const COUNT_MOBILE = 90;

export interface PetalScene {
	render: () => void;
	resize: (w: number, h: number) => void;
	setMouse: (nx: number, ny: number) => void;
	gust: (factor: number, durationMs?: number) => void;
	emitBurst: (worldX: number, worldY: number, count?: number) => void;
	camera: THREE.PerspectiveCamera;
	dispose: () => void;
	canvasToWorld: (clientX: number, clientY: number) => THREE.Vector3;
}

export function initPetalScene(canvas: HTMLCanvasElement): PetalScene | null {
	const reducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	const isMobile = window.matchMedia("(max-width: 768px)").matches;
	const baseCount = isMobile ? COUNT_MOBILE : COUNT_DESKTOP;
	let petalCount = baseCount; // grows when bursts emit extras

	let renderer: THREE.WebGLRenderer;
	try {
		renderer = new THREE.WebGLRenderer({
			canvas,
			antialias: true,
			powerPreference: "high-performance",
			alpha: false,
			stencil: false,
		});
	} catch {
		return null;
	}
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.15;
	renderer.setPixelRatio(isMobile ? 1 : Math.min(window.devicePixelRatio, 2));

	const parent = canvas.parentElement!;
	const sizeToParent = () => {
		const r = parent.getBoundingClientRect();
		renderer.setSize(r.width, r.height, false);
		return { w: r.width, h: r.height };
	};
	const { w: w0, h: h0 } = sizeToParent();

	const scene = new THREE.Scene();
	scene.background = new THREE.Color("#1a1518");
	scene.fog = new THREE.Fog("#1a1518", 8, 22);

	const camera = new THREE.PerspectiveCamera(45, w0 / h0, 0.1, 50);
	camera.position.set(0, 0.5, 6);
	camera.lookAt(0, 0, 0);

	// Lights
	const sun = new THREE.DirectionalLight("#fff5e0", 1.8);
	sun.position.set(3, 4, 4);
	scene.add(sun);

	const coolFill = new THREE.DirectionalLight("#a8d5b8", 0.5);
	coolFill.position.set(-3, 1, 2);
	scene.add(coolFill);

	scene.add(new THREE.AmbientLight("#3a2a30", 0.4));

	// Petal geometry — teardrop via bezier outline
	const shape = new THREE.Shape();
	shape.moveTo(0, 0);
	shape.bezierCurveTo(0.18, 0.05, 0.22, 0.35, 0, 0.6);
	shape.bezierCurveTo(-0.22, 0.35, -0.18, 0.05, 0, 0);
	const geometry = new THREE.ShapeGeometry(shape, 12);
	// center the geometry roughly
	geometry.translate(0, -0.3, 0);
	geometry.computeVertexNormals();

	const material = new THREE.MeshStandardMaterial({
		side: THREE.DoubleSide,
		roughness: 0.6,
		metalness: 0.0,
		transparent: true,
		opacity: 0.92,
		vertexColors: false,
	});

	// Max capacity = baseCount + reserve for bursts
	const MAX_PETALS = baseCount + 90;
	const mesh = new THREE.InstancedMesh(geometry, material, MAX_PETALS);
	mesh.frustumCulled = false;
	mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

	const colorAttr = new THREE.InstancedBufferAttribute(
		new Float32Array(MAX_PETALS * 3),
		3,
	);
	mesh.instanceColor = colorAttr;
	scene.add(mesh);

	const data: PetalDatum[] = [];

	const SPAWN_RADIUS_X = 6;
	const SPAWN_RADIUS_Y = 4;
	const SPAWN_DEPTH = 5; // z range -5..0 (closer to camera => sharper focus)

	function makePetal(initial: boolean): PetalDatum {
		const x = (Math.random() - 0.5) * 2 * SPAWN_RADIUS_X;
		const y = initial
			? (Math.random() - 0.5) * 2 * SPAWN_RADIUS_Y
			: SPAWN_RADIUS_Y + Math.random() * 2;
		const z = -Math.random() * SPAWN_DEPTH;
		return {
			pos: new THREE.Vector3(x, y, z),
			vel: new THREE.Vector3(
				(Math.random() - 0.5) * 0.12,
				-0.18 - Math.random() * 0.22,
				(Math.random() - 0.5) * 0.04,
			),
			rot: new THREE.Euler(
				Math.random() * Math.PI * 2,
				Math.random() * Math.PI * 2,
				Math.random() * Math.PI * 2,
			),
			rotSpeed: new THREE.Vector3(0.6, 0.4, 0.2),
			scale: 0.7 + Math.random() * 0.5,
			phase: Math.random() * Math.PI * 2,
			freq: 0.6 + Math.random() * 0.6,
		};
	}

	const tmpMat = new THREE.Matrix4();
	const tmpQuat = new THREE.Quaternion();
	const tmpScale = new THREE.Vector3();
	const tmpEuler = new THREE.Euler();

	for (let i = 0; i < baseCount; i++) {
		const d = makePetal(true);
		data.push(d);
		const col = pickColor();
		colorAttr.setXYZ(i, col.r, col.g, col.b);
	}
	// fill the rest with hidden petals (scale 0)
	for (let i = baseCount; i < MAX_PETALS; i++) {
		data.push({
			pos: new THREE.Vector3(0, -999, 0),
			vel: new THREE.Vector3(),
			rot: new THREE.Euler(),
			rotSpeed: new THREE.Vector3(),
			scale: 0,
			phase: 0,
			freq: 1,
		});
		colorAttr.setXYZ(i, 1, 1, 1);
	}
	colorAttr.needsUpdate = true;

	// Composer + effects.
	// Splitting DoF and Bloom into separate EffectPasses avoids
	// glBlitFramebuffer "same depth attachment" warnings, since DoF needs
	// to sample depth while Bloom writes color back into the same buffer.
	const composer = new EffectComposer(renderer, {
		frameBufferType: THREE.HalfFloatType,
	});
	composer.addPass(new RenderPass(scene, camera));

	const bloom = new BloomEffect({
		intensity: 0.55,
		luminanceThreshold: 0.7,
		radius: 0.8,
	});
	const dof = new DepthOfFieldEffect(camera, {
		focusDistance: 0.02,
		focalLength: 0.045,
		bokehScale: 3.0,
	});
	const vignette = new VignetteEffect({ darkness: 0.4, offset: 0.4 });
	const bc = new BrightnessContrastEffect({ brightness: 0.02, contrast: 0.05 });

	// DoF in its own pass (needs depth), then bloom + vignette + contrast
	composer.addPass(new EffectPass(camera, dof));
	composer.addPass(new EffectPass(camera, bloom, vignette, bc));

	// Mouse parallax
	const mouseTarget = new THREE.Vector2(0, 0);
	const mouseLerp = new THREE.Vector2(0, 0);

	// Wind gust
	let windFactor = 1;
	let windExpire = 0;

	const clock = new THREE.Clock();

	function update(dt: number, t: number) {
		const wf = windFactor;

		for (let i = 0; i < petalCount; i++) {
			const d = data[i];
			if (d.scale <= 0) continue;

			// drift: sinusoidal x-y sway + base vel
			const sway = Math.sin(t * d.freq + d.phase);
			d.pos.x += (d.vel.x + sway * 0.18) * dt * wf;
			d.pos.y += d.vel.y * dt * wf;
			d.pos.z += (d.vel.z + Math.cos(t * d.freq * 0.7 + d.phase) * 0.05) * dt;

			// rotation tumble
			d.rot.x += d.rotSpeed.x * dt;
			d.rot.y += d.rotSpeed.y * dt;
			d.rot.z += d.rotSpeed.z * dt * 0.3;

			// respawn when far from origin
			if (d.pos.length() > 12 || d.pos.y < -SPAWN_RADIUS_Y - 1.5) {
				if (i < baseCount) {
					// regular petal — respawn above
					const fresh = makePetal(false);
					d.pos.copy(fresh.pos);
					d.vel.copy(fresh.vel);
					d.rot.copy(fresh.rot);
					d.scale = fresh.scale;
					d.phase = fresh.phase;
					d.freq = fresh.freq;
				} else {
					// burst petal — recycle (hide)
					d.scale = 0;
					d.pos.set(0, -999, 0);
				}
			}

			tmpEuler.copy(d.rot);
			tmpQuat.setFromEuler(tmpEuler);
			tmpScale.setScalar(d.scale);
			tmpMat.compose(d.pos, tmpQuat, tmpScale);
			mesh.setMatrixAt(i, tmpMat);
		}
		mesh.instanceMatrix.needsUpdate = true;

		// wind expiry
		if (windExpire > 0) {
			windExpire -= dt * 1000;
			if (windExpire <= 0) windFactor = 1;
		}
	}

	function applyParallax(dt: number) {
		mouseLerp.lerp(mouseTarget, Math.min(1, dt * 4));
		camera.position.x = mouseLerp.x * 0.3;
		camera.position.y = 0.5 + mouseLerp.y * 0.2;
		camera.lookAt(0, 0, 0);
	}

	let time = 0;
	let raf = 0;

	function render() {
		const dt = Math.min(clock.getDelta(), 0.05);
		time += dt;
		if (!reducedMotion) {
			update(dt, time);
			applyParallax(dt);
		}
		composer.render(dt);
		raf = requestAnimationFrame(render);
	}

	function resize(w: number, h: number) {
		renderer.setSize(w, h, false);
		composer.setSize(w, h);
		camera.aspect = w / h;
		camera.updateProjectionMatrix();
	}

	function setMouse(nx: number, ny: number) {
		mouseTarget.set(nx, ny);
	}

	function gust(factor: number, durationMs = 600) {
		windFactor = factor;
		windExpire = durationMs;
	}

	function canvasToWorld(clientX: number, clientY: number): THREE.Vector3 {
		const rect = canvas.getBoundingClientRect();
		const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
		const ndcY = -(((clientY - rect.top) / rect.height) * 2 - 1);
		const v = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(camera);
		const dir = v.sub(camera.position).normalize();
		const dist = -camera.position.z / dir.z;
		return camera.position.clone().add(dir.multiplyScalar(dist));
	}

	function emitBurst(worldX: number, worldY: number, count = 15) {
		let emitted = 0;
		for (let i = baseCount; i < MAX_PETALS && emitted < count; i++) {
			const d = data[i];
			if (d.scale > 0) continue;
			d.pos.set(worldX, worldY, 0.2 + Math.random() * 0.4);
			const angle = Math.random() * Math.PI * 2;
			const speed = 1.2 + Math.random() * 1.6;
			d.vel.set(
				Math.cos(angle) * speed,
				Math.sin(angle) * speed + 0.6,
				(Math.random() - 0.5) * 0.4,
			);
			d.rot.set(
				Math.random() * Math.PI * 2,
				Math.random() * Math.PI * 2,
				Math.random() * Math.PI * 2,
			);
			d.rotSpeed.set(2, 1.4, 0.6);
			d.scale = 0.6 + Math.random() * 0.5;
			d.phase = Math.random() * Math.PI * 2;
			d.freq = 1.2 + Math.random();
			const col = pickColor();
			colorAttr.setXYZ(i, col.r, col.g, col.b);
			emitted++;
		}
		colorAttr.needsUpdate = true;
		// expand active range so update() touches burst petals
		petalCount = MAX_PETALS;
		// shrink back when bursts finish — handled via the scale<=0 short-circuit
	}

	raf = requestAnimationFrame(render);

	function dispose() {
		cancelAnimationFrame(raf);
		composer.dispose();
		renderer.dispose();
		geometry.dispose();
		material.dispose();
	}

	return {
		render,
		resize,
		setMouse,
		gust,
		emitBurst,
		camera,
		dispose,
		canvasToWorld,
	};
}
